import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Refresh,
  ChevronRight,
  WatchLater,
  EventAvailable,
  UpdateOutlined,
  CheckCircleOutline,
  NoteAlt,
} from '@mui/icons-material';
import { PageHeader, StatCard, TypePill, ErrorState } from '../../components/common/pageKit';
import crmService, {
  MyDay,
  MyDayItem,
  CrmActivity,
  ActivityType,
  ACTIVITY_TYPES,
  CreateActivityInput,
} from '../../services/crmService';

/** Identity colour per activity type — decorative, distinct from status colour. */
const ACTIVITY_TINT: Record<ActivityType, string> = {
  CALL: '#0284c7',
  EMAIL: '#7c3aed',
  MEETING: '#059669',
  NOTE: '#64748b',
  TASK: '#ea580c',
  DEMO: '#db2777',
};

const fmtWhen = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  return isToday ? time : `${d.toLocaleDateString('en-IN')} · ${time}`;
};

interface LogFormData {
  activityType: ActivityType;
  subject: string;
  outcome: string;
  notes: string;
  followUpAt: string;
}

const emptyLogForm: LogFormData = {
  activityType: 'CALL',
  subject: '',
  outcome: '',
  notes: '',
  followUpAt: '',
};

const MyDayPage: React.FC = () => {
  const navigate = useNavigate();

  const [myDay, setMyDay] = useState<MyDay | null>(null);
  const [upcoming, setUpcoming] = useState<CrmActivity[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [logTarget, setLogTarget] = useState<MyDayItem | null>(null);
  const [logForm, setLogForm] = useState<LogFormData>(emptyLogForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    crmService
      .getMyDay()
      .then(setMyDay)
      .catch((e: any) => setError(e?.message || 'Failed to load My Day.'))
      .finally(() => setLoading(false));

    crmService
      .getUpcomingActivities()
      .then((r) => setUpcoming(r.activities))
      .catch(() => setUpcoming(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const overdueItems = useMemo(() => (myDay?.items || []).filter((i) => i.bucket === 'overdue'), [myDay]);
  const dueTodayItems = useMemo(() => (myDay?.items || []).filter((i) => i.bucket === 'dueToday'), [myDay]);
  const allCaughtUp = !loading && !error && overdueItems.length === 0 && dueTodayItems.length === 0;

  const openLogDialog = (item: MyDayItem) => {
    setLogTarget(item);
    setLogForm({
      activityType: item.activityType,
      subject: item.subject || '',
      outcome: '',
      notes: '',
      followUpAt: '',
    });
  };

  const handleLogFollowUp = async () => {
    if (!logTarget) return;
    setSaving(true);
    try {
      const payload: CreateActivityInput = {
        entityType: logTarget.entityType,
        entityId: logTarget.entityId,
        activityType: logForm.activityType,
        subject: logForm.subject,
        outcome: logForm.outcome || undefined,
        notes: logForm.notes || undefined,
        followUpAt: logForm.followUpAt || undefined,
      };
      await crmService.createActivity(payload);
      setToast({ message: 'Follow-up logged', severity: 'success' });
      setLogTarget(null);
      load();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to log follow-up', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const Row: React.FC<{ item: MyDayItem; accent: string }> = ({ item, accent }) => (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: accent,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 96, color: 'text.secondary' }}
      >
        {fmtWhen(item.followUpAt)}
      </Typography>

      <Box sx={{ minWidth: 160, flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {item.entityLabel || '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
          {item.entityType}
        </Typography>
      </Box>

      <TypePill label={item.activityType} tint={ACTIVITY_TINT[item.activityType] || '#64748b'} />

      <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }} noWrap>
        {item.subject}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Button
          size="small"
          endIcon={<ChevronRight />}
          disabled={!item.link}
          onClick={() => item.link && navigate(item.link)}
        >
          Open
        </Button>
        <Button size="small" variant="outlined" startIcon={<NoteAlt fontSize="small" />} onClick={() => openLogDialog(item)}>
          Log follow-up
        </Button>
      </Stack>
    </Paper>
  );

  const Section: React.FC<{ title: string; items: MyDayItem[]; accent: string }> = ({ title, items, accent }) =>
    items.length === 0 ? null : (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
          {title} ({items.length})
        </Typography>
        <Stack spacing={1}>
          {items.map((item) => (
            <Row key={item.id} item={item} accent={accent} />
          ))}
        </Stack>
      </Box>
    );

  return (
    <Box>
      <PageHeader
        title="My Day"
        subtitle="Your follow-ups, in order of urgency"
        actions={
          <IconButton onClick={load} disabled={loading}>
            <Refresh />
          </IconButton>
        }
      />

      {loading && !myDay ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : error && !myDay ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            <StatCard title="Overdue" value={myDay?.counts.overdue ?? null} icon={<WatchLater />} color="#d03b3b" />
            <StatCard title="Due Today" value={myDay?.counts.dueToday ?? null} icon={<EventAvailable />} color="#fab219" />
            <StatCard title="Upcoming" value={myDay?.counts.upcoming ?? null} icon={<UpdateOutlined />} color="#0284c7" />
          </Box>

          {allCaughtUp ? (
            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CheckCircleOutline sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.5 }}>
                You're all caught up
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No overdue or due-today follow-ups right now.
              </Typography>
            </Paper>
          ) : (
            <>
              <Section title="Overdue" items={overdueItems} accent="#d03b3b" />
              <Section title="Due Today" items={dueTodayItems} accent="#fab219" />
            </>
          )}

          {/* Upcoming */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
              Upcoming
            </Typography>
            {upcoming === null ? (
              <Typography variant="body2" color="text.secondary">
                Upcoming activities are unavailable right now.
              </Typography>
            ) : upcoming.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Nothing scheduled beyond today.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {upcoming.map((a) => (
                  <Paper
                    key={a.id}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 96, color: 'text.secondary' }}
                    >
                      {fmtWhen(a.follow_up_at)}
                    </Typography>
                    <TypePill label={a.activity_type} tint={ACTIVITY_TINT[a.activity_type] || '#64748b'} />
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }} noWrap>
                      {a.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {a.owner_name || '—'}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </>
      )}

      {/* Log follow-up dialog */}
      <Dialog open={!!logTarget} onClose={() => setLogTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Follow-up</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {logTarget && (
              <Typography variant="body2" color="text.secondary">
                {logTarget.entityLabel || `${logTarget.entityType} #${logTarget.entityId}`}
              </Typography>
            )}
            <TextField
              select
              fullWidth
              label="Activity Type"
              value={logForm.activityType}
              onChange={(e) => setLogForm((f) => ({ ...f, activityType: e.target.value as ActivityType }))}
            >
              {ACTIVITY_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Subject"
              value={logForm.subject}
              onChange={(e) => setLogForm((f) => ({ ...f, subject: e.target.value }))}
              required
            />
            <TextField
              fullWidth
              label="Outcome"
              placeholder="What happened?"
              value={logForm.outcome}
              onChange={(e) => setLogForm((f) => ({ ...f, outcome: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={logForm.notes}
              onChange={(e) => setLogForm((f) => ({ ...f, notes: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Next Follow-up"
              type="datetime-local"
              value={logForm.followUpAt}
              onChange={(e) => setLogForm((f) => ({ ...f, followUpAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText="Optional — sets when this comes back onto My Day"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setLogTarget(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleLogFollowUp} disabled={saving || !logForm.subject.trim()}>
            {saving ? 'Saving…' : 'Log Follow-up'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default MyDayPage;
